import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Flex,
  Group,
  Paper,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconAdjustments,
  IconBulb,
  IconChevronDown,
  IconChevronUp,
  IconCirclePlus,
  IconCopy,
  IconPaperclip,
  IconPhoto,
  IconPlayerStop,
  IconSend,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useAiChatScroll from '../../../helpers/useAiChatScroll';
import usePrevious from '../../../helpers/usePrevious';
import ErrorMessage from '../../ErrorMessage';
import AiChatDocumentsModal from '../../Modals/AiChatDocumentsModal';
import AiChatImagesPickModal from '../../Modals/AiChatImagesPickModal';
import AiChatImagesUploadModal from '../../Modals/AiChatImagesUploadModal';

import AiChatFastContextModal from '../../Modals/AiChatFastContextModal';
import AdvancedImagePromptSettings from '../AdvancedImagePromptSettings';
import AdvancedTextPromptSettings from '../AdvancedTextPromptSettings';
import PromptInput from '../PromptInput/PromptInput';
import PromptResponseList from '../PromptResponseList/PromptResponseList';
import './AiChat.css';

const getInitialData = (data) => {
  return data ? JSON.parse(data) : { responseList: [], prompt: '' };
};

const AiChat = ({
  generatedContent,
  setData,
  brandContext,
  handleBrandContextChange,
  generationType,
  setGenerationType,
}) => {
  const MAX_TOKENS = 29000;
  const CHARS_COUNT_PER_TOKEN = 4;

  const { responseList: initialResponseList, prompt: initialPrompt } = getInitialData(generatedContent.data);
  const [responseList, setResponseList] = useState(initialResponseList);
  const [promptAddition, setPromptAddition] = useState('');
  const [promptToRetry, setPromptToRetry] = useState(null);
  const [uniqueIdToRetry, setUniqueIdToRetry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { domain } = useSelector((state) => state.domain);
  const [fastContextOpen, setFastContextOpen] = useState(false);
  const [fastContextSelection, setFastContextSelection] = useState();
  const [imageUrl, setImageUrl] = useState();
  const [error, setError] = useState();
  const [userError, setUserError] = useState();
  const previousId = usePrevious(generatedContent.id);
  const [advancedSettings, setAdvancedSettings] = useState({
    // Used for image generation
    safeImageGeneration: true,
    prompt_strength: 80,
    seed: 100000,
    // Used for text generation
    temperature: 100,
    top_p: 95,
    system_prompt: 'You are a helpful assistant.',
    aspect_ratio: '1:1',
    width: null,
    height: null,
  });
  const [inputDisabled, setInputDisabled] = useState(false);
  const [isAbortButtonVisible, setIsAbortButtonVisible] = useState(false);
  const [inputTooLong, setInputTooLong] = useState(false);
  const [contextTooLong, setContextTooLong] = useState(false);
  const [openSettingsTab, setOpenSettingsTab] = useState(null);
  const controllerRef = useRef(null);
  const scrollRef = useRef(null);

  const location = useLocation();

  const [activeTab, setActiveTab] = useState(generationType);
  const [selectedText, setSelectedText] = useState('');
  const [showSnippetButton, setShowSnippetButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const chatContainerRef = useRef(null);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [imagesUploadModalOpen, setImagesUploadModalOpen] = useState(false);
  const [imagesPickModalOpen, setImagesPickModalOpen] = useState(false);

  const aiChatScroll = useAiChatScroll(scrollRef, responseList);

  const handleTabClick = (type) => {
    setGenerationType(type);
    setActiveTab(type);
  };

  useEffect(() => {
    setOpenSettingsTab(false);
    setError();
    setUserError();
    setIsLoading(false);
    setActiveTab(generationType);
  }, [location, generationType]);

  useEffect(() => {
    const handleTextSelection = () => {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();

      if (selectedText && chatContainerRef.current) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const chatContainerRect = chatContainerRef.current.getBoundingClientRect();

        const top = rect.top - chatContainerRect.top;
        const left = rect.left - chatContainerRect.left;

        setButtonPosition({ top: top - 40, left: left + 10 });
        setShowSnippetButton(true);
      } else {
        setShowSnippetButton(false);
      }
    };

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('mouseup', handleTextSelection);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener('mouseup', handleTextSelection);
      }
    };
  }, []);

  const getSelectedText = () => {
    const selection = window.getSelection();

    if (!selection) {
      return '';
    }

    return selection.toString().trim();
  };

  const handleAddSnippet = () => {
    const selectedText = getSelectedText();
    setSelectedText(selectedText);
    setShowSnippetButton(false);
    setFastContextOpen(true);
  };

  const toggleAdvancedSettings = () => {
    setOpenSettingsTab(openSettingsTab === activeTab ? null : activeTab);
  };

  const toggleDocumentsModal = () => {
    setDocumentsModalOpen(!documentsModalOpen);
  };

  const toggleImagesUploadModal = () => {
    setImagesUploadModalOpen(!imagesUploadModalOpen);
  };
  const toggleImagesPickModal = () => {
    setImagesPickModalOpen(!imagesPickModalOpen);
  };

  useEffect(() => {
    if (JSON.stringify(initialResponseList) !== JSON.stringify(responseList)) {
      setData(JSON.stringify({ responseList, initialPrompt }));
    }
  }, [responseList, setData, initialResponseList, initialPrompt]);

  // handle change of underlying data
  useEffect(() => {
    if (previousId !== generatedContent.id) {
      const { responseList } = getInitialData(generatedContent.data);
      setResponseList(responseList);
    }
    // we only want to update when the id has changed
  }, [generatedContent, previousId]);

  let routeEndpoint = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/aiPrompts`;

  const generateUniqueId = () => {
    const timestamp = Date.now();
    const randomNumber = Math.random();
    const hexadecimalString = randomNumber.toString(16);

    return `id-${timestamp}-${hexadecimalString}`;
  };

  const delay = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const addResponse = (selfFlag, response) => {
    const uid = generateUniqueId();
    setResponseList((prevResponses) => [
      ...prevResponses,
      {
        id: uid,
        response,
        selfFlag,
      },
    ]);
    return uid;
  };

  const addFile = (name, url) => {
    setResponseList((prevResponses) => [
      ...prevResponses,
      {
        id: generateUniqueId(),
        file: {
          name,
          url,
        },
        selfFlag: true,
      },
    ]);
  };

  const addDescribeResponse = (url) => {
    const uid = generateUniqueId();
    setResponseList((prevResponses) => [
      ...prevResponses,
      {
        id: uid,
        selfFlag: true,
        image: url,
      },
    ]);
    return uid;
  };

  const updateResponse = (uid, updatedObject) => {
    setResponseList((prevResponses) => {
      const updatedList = [...prevResponses];
      const index = prevResponses.findIndex((response) => response.id === uid);
      if (index > -1) {
        updatedList[index] = {
          ...updatedList[index],
          ...updatedObject,
        };
      }
      return updatedList;
    });
  };

  const updateResponseByChunk = (chunk) => {
    setResponseList((prevResponses) => {
      const updatedList = [...prevResponses];
      const index = prevResponses.length - 1;
      if (index > -1) {
        updatedList[index] = {
          ...updatedList[index],
          response: (updatedList[index].response || '') + chunk,
        };
      }
      return updatedList;
    });
  };

  const regenerateResponse = async () => {
    await getGPTResult(promptToRetry, uniqueIdToRetry);
  };

  const createTextRequestBody = (_prompt) => {
    const messagesWithLimitedLength = [
      ...responseList.map((r) => {
        const res = {
          content: r.response || r.image,
          role: r.selfFlag ? 'user' : 'assistant',
        };

        if (r.file) {
          res.file = true;
          res.content = r.file.url;
        }

        return res;
      }),
      { role: 'user', content: _prompt },
    ].filter((r) => r.content);

    if (messagesWithLimitedLength.length) {
      let countOfTokens = messagesWithLimitedLength.map((r) => r.content).join('').length / CHARS_COUNT_PER_TOKEN;

      if (countOfTokens > MAX_TOKENS) {
        setContextTooLong(true);
      }
    }

    const body = {
      model: generatedContent.model,
      provider: generatedContent.provider,
      messages: messagesWithLimitedLength,
      modelParams: {
        top_p: advancedSettings.top_p,
        temperature: advancedSettings.temperature,
        system_prompt: advancedSettings.system_prompt,
      },
      contextSelection: { ...fastContextSelection },
      domainName: domain.name,
    };

    return body;
  };

  const createImageRequestBody = (_prompt) => {
    const requestBody = {
      messages: [{ role: 'user', content: _prompt }],
      safeImageGeneration: advancedSettings.safeImageGeneration,
      modelParams: {
        seed: advancedSettings.seed,
        prompt_strength: advancedSettings.prompt_strength,
        aspect_ratio: advancedSettings.aspect_ratio,
        width: advancedSettings.width,
        height: advancedSettings.height,
      },
    };

    if (imageUrl) {
      requestBody.refImage = imageUrl;
    }

    return requestBody;
  };

  const createDescribeRequestBody = (_prompt) => {
    const requestBody = {
      messages: [{ role: 'user', content: _prompt }],
      refImage: imageUrl,
      modelParams: {
        top_p: advancedSettings.top_p,
        temperature: advancedSettings.temperature,
      },
    };

    return requestBody;
  };

  const finalizeGPTRequest = (skipRetryRest) => {
    if (skipRetryRest) {
      setIsLoading(false);
      setInputDisabled(false);
      setIsAbortButtonVisible(false);
    } else {
      setPromptToRetry(null);
      setUniqueIdToRetry(null);
      setIsLoading(false);
      setInputDisabled(false);
      setIsAbortButtonVisible(false);
    }
  };

  const getGPTResult = async (prompt, _uniqueIdToRetry) => {
    // Get the prompt input
    let _prompt = prompt ?? prompt;

    // If a response is already being generated or the prompt is empty, return
    if (!(generationType === 'describe' && imageUrl) && (isLoading || !_prompt)) {
      return;
    }

    setIsAbortButtonVisible(true);
    setIsLoading(true);
    setError(false);

    let uniqueId;
    if (_uniqueIdToRetry) {
      uniqueId = _uniqueIdToRetry;
    } else {
      // Add the self prompt to the response list

      if (generationType === 'describe') {
        if (imageUrl) {
          addDescribeResponse(imageUrl);
        }
      }

      addResponse(true, _prompt);
      uniqueId = addResponse(false);
      await delay(50);
    }

    try {
      let requestBody;

      if (generationType === 'text') {
        requestBody = createTextRequestBody(_prompt);
      } else if (generationType === 'image') {
        requestBody = createImageRequestBody(_prompt);
        routeEndpoint += '/image';
      } else if (generationType === 'describe') {
        requestBody = createDescribeRequestBody(_prompt);
        routeEndpoint += '/describe';
      }

      setImageUrl('');

      setInputDisabled(true);
      setOpenSettingsTab(false);

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const response = await fetch(routeEndpoint, {
        method: 'POST',
        headers: {
          'content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      setIsLoading(false);

      if (!response.ok) {
        const staticError = `Error: ${response.statusText}`;

        if (response.status === 504) {
          updateResponse(uniqueId, {
            response: 'Oops...The AI model is at capacity right now. Please try again later or use another model.',
            error: true,
          });

          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let result;
        let errorString = '';

        while (!result?.done) {
          result = await reader.read();
          let chunk = decoder.decode(result.value);
          errorString += chunk;
        }

        let finalError;
        try {
          const errorMessage = JSON.parse(errorString)?.message;
          finalError = errorMessage || staticError;
        } catch (err) {
          finalError = staticError;
        }

        updateResponse(uniqueId, {
          response: finalError,
          error: true,
        });

        return;
      }

      if (generationType === 'text') {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result;
        while (!result?.done) {
          result = await reader.read();
          let chunk = decoder.decode(result.value);
          if (chunk) {
            updateResponseByChunk(chunk);
          }
        }
      } else if (generationType === 'image') {
        const { response: jsonResponse } = await response.json();
        // Show image for `Create image` model
        updateResponse(uniqueId, {
          image: jsonResponse,
        });
      } else if (generationType === 'describe') {
        let { response: text } = await response.json();

        updateResponse(uniqueId, { response: text });
      }
    } catch (err) {
      // aborting the stream emits AbortError; not to be displayed in the UI
      if (err.name !== 'AbortError' && err !== 'User Aborted Streaming') {
        setPromptToRetry(_prompt);
        setUniqueIdToRetry(uniqueId);
        setError(`Error: ${err.message ? err.message : err.toString()}`);
      }
    } finally {
      if (promptToRetry || uniqueIdToRetry) {
        finalizeGPTRequest(true);
      }
      finalizeGPTRequest();
    }
  };

  const abortStream = () => {
    controllerRef.current.abort('User Aborted Streaming');
    setIsAbortButtonVisible(false);
  };

  const settingsLabel = (type) => {
    switch (type) {
      case 'image':
        return 'Image';
      case 'text':
        return 'Text';
      case 'describe':
        return 'Vision';
      default:
        return 'Unknown';
    }
  };

  const copyText = (text) => {
    if (text && text !== '' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setShowSnippetButton(false);
    }
  };

  const closeContextModal = () => {
    setFastContextOpen(false);

    setFastContextSelection((prev) => {
      if (!prev) {
        return {};
      }

      const contextSelection = structuredClone(prev);
      delete contextSelection.coPilots;
      setPromptAddition(
        Object.entries(contextSelection)
          .map(([key, values]) => {
            if (!values || !values.length) {
              return '';
            }

            if (key === 'userPrompts') {
              return values.map((v) => v.description + '\n\n').join('');
            }

            return `${key[0].toUpperCase() + key.slice(1)}:\n${values.join('\n')}\n\n`;
          })
          .join('')
      );

      const newContextSelection = structuredClone(prev);
      newContextSelection.archetypes = [];
      newContextSelection.audiences = [];
      newContextSelection.description = [];
      newContextSelection.tones = [];
      newContextSelection.voices = [];
      newContextSelection.userPrompts = [];

      return newContextSelection;
    });
  };

  let activeCoPilot = false;
  if (fastContextSelection?.coPilots?.length && fastContextSelection.coPilots[0] !== 'No Co-pilot') {
    activeCoPilot = fastContextSelection.coPilots[0];
  }

  return (
    <Box>
      {/* Chat Response Area */}
      <Paper
        ref={chatContainerRef}
        withBorder
        radius="sm"
        mb="xs"
        style={{ height: '60vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <Box ref={scrollRef} onScroll={aiChatScroll} style={{ flex: 1, overflowY: 'auto' }}>
          <PromptResponseList
            responseList={responseList}
            isLoading={isLoading}
            key="response-list"
            type="chat"
            onQuickReply={(instruction) => getGPTResult(instruction)}
          />
        </Box>
      </Paper>

      <ErrorMessage message={error} />

      {/* Regenerate Button */}
      {uniqueIdToRetry && (
        <Flex justify="center" mb="sm">
          <Button
            color="teal"
            loading={isLoading}
            onClick={() => regenerateResponse()}
          >
            Regenerate Response
          </Button>
        </Flex>
      )}

      {/* Abort Button */}
      {isAbortButtonVisible && (
        <Flex direction="column" align="center" my="sm">
          {isLoading && generatedContent.provider === 'rlv' && (
            <Text size="sm" c="dimmed" mb="xs">
              Our advanced language model is designed to work with a wide range of prompts, but for longer or more
              detailed inputs, it may require extra time to generate a thorough response.
            </Text>
          )}
          <Button leftSection={<IconPlayerStop size={14} />} onClick={abortStream}>
            Stop generating
          </Button>
        </Flex>
      )}

      {/* Context Too Long Warning */}
      {contextTooLong && (
        <Flex direction="column" align="center" my="sm">
          <Text size="sm" c="dimmed">
            Note: This chat is getting quite long. To maintain the quality of this discussion, it would be best to
            continue in a fresh conversation shortly.
          </Text>
        </Flex>
      )}

      {/* Tabs and Advanced Settings */}
      <Flex justify="space-between" align="center" mt="sm">
        <Tabs value={activeTab} onChange={handleTabClick} variant="default">
          <Tabs.List>
            <Tabs.Tab value="text">Text</Tabs.Tab>
            <Tabs.Tab value="describe">Vision</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Button
          variant="default"
          onClick={toggleAdvancedSettings}
          rightSection={openSettingsTab === activeTab ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          leftSection={<IconAdjustments size={14} />}
        >
          Advanced {settingsLabel(generationType)} Settings
        </Button>
      </Flex>

      {/* Advanced Settings Panel */}
      <Collapse expanded={openSettingsTab === activeTab}>
        <Card withBorder mt={0} radius={0} style={{ borderTop: 'none' }}>
          <Card.Section inheritPadding py="sm">
            {(activeTab === 'text' || activeTab === 'describe') && (
              <AdvancedTextPromptSettings
                advancedSettings={advancedSettings}
                setAdvancedSettings={setAdvancedSettings}
                visible={openSettingsTab === activeTab}
                generationType={generationType}
                systemPromptVisible={!activeCoPilot}
              />
            )}

            {activeTab === 'image' && (
              <AdvancedImagePromptSettings
                advancedSettings={advancedSettings}
                setAdvancedSettings={setAdvancedSettings}
                visible={openSettingsTab === activeTab}
                generationType={generationType}
              />
            )}
          </Card.Section>
        </Card>
      </Collapse>

      {/* Active Co-Pilot Badge */}
      {activeCoPilot && (
        <Box mt="sm">
          <Badge
            variant="light"
            color="green"
            style={{ cursor: 'pointer' }}
            onClick={() => setFastContextOpen(true)}
          >
            {activeCoPilot} - Active
          </Badge>
        </Box>
      )}

      {/* Input Container */}
      <Paper
        withBorder
        radius="sm"
        p="xs"
        mt="sm"
        style={{ opacity: inputDisabled ? 0.5 : 1, pointerEvents: inputDisabled ? 'none' : 'auto' }}
      >
        <Flex align="center" gap="xs">
          <Tooltip label="Context & Prompts">
            <ActionIcon
              variant="subtle"
              color="primary"
              onClick={() => setFastContextOpen(true)}
              disabled={!!inputTooLong}
            >
              <IconBulb size={20} />
            </ActionIcon>
          </Tooltip>

          {generationType === 'text' && (
            <Tooltip label="Attach Document">
              <ActionIcon
                variant="subtle"
                color="primary"
                onClick={toggleDocumentsModal}
                disabled={!!inputTooLong}
              >
                <IconPaperclip size={20} />
              </ActionIcon>
            </Tooltip>
          )}

          {generationType === 'describe' && (
            <>
              <Tooltip label="Upload Image">
                <ActionIcon
                  variant="subtle"
                  color="primary"
                  onClick={toggleImagesUploadModal}
                  disabled={!!inputTooLong}
                >
                  <IconPaperclip size={20} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Pick Image">
                <ActionIcon
                  variant={imageUrl ? 'filled' : 'subtle'}
                  color="primary"
                  onClick={toggleImagesPickModal}
                >
                  <IconPhoto size={20} />
                </ActionIcon>
              </Tooltip>
            </>
          )}

          <Box style={{ flex: 1 }}>
            <PromptInput
              promptAddition={promptAddition}
              setPromptAddition={setPromptAddition}
              onSubmit={getGPTResult}
              setInputTooLong={setInputTooLong}
              maxTokens={MAX_TOKENS}
              disabled={inputTooLong || isLoading}
            />
          </Box>
        </Flex>
      </Paper>

      {inputTooLong && <ErrorMessage message={inputTooLong} />}

      {/* Snippet Selection Buttons */}
      {showSnippetButton && (
        <Group
          gap="xs"
          style={{
            position: 'absolute',
            top: `${buttonPosition.top}px`,
            left: `${buttonPosition.left}px`,
            zIndex: 1000,
          }}
        >
          <Button size="xs" leftSection={<IconCirclePlus size={14} />} onClick={handleAddSnippet}>
            Save Custom Prompt
          </Button>
          <ActionIcon variant="filled" onClick={() => copyText(getSelectedText())}>
            <IconCopy size={14} />
          </ActionIcon>
        </Group>
      )}

      {/* Modals */}
      {fastContextOpen && (
        <AiChatFastContextModal
          brandContext={brandContext}
          selectedContext={fastContextSelection}
          setSelectedContext={setFastContextSelection}
          closeModal={closeContextModal}
          handleBrandContextChange={handleBrandContextChange}
          selectedText={selectedText}
          setSelectedText={setSelectedText}
        />
      )}

      {documentsModalOpen && (
        <AiChatDocumentsModal
          documentsModalOpen={documentsModalOpen}
          toggleDocumentsModal={toggleDocumentsModal}
          addFile={(name, url) => addFile(name, url)}
        />
      )}

      {imagesUploadModalOpen && (
        <AiChatImagesUploadModal
          imagesUploadModalOpen={imagesUploadModalOpen}
          toggleImagesUploadModal={toggleImagesUploadModal}
          addFile={(url) => setPromptAddition(url)}
        />
      )}

      {imagesPickModalOpen && (
        <AiChatImagesPickModal
          imagesPickModalOpen={imagesPickModalOpen}
          toggleImagesPickModal={toggleImagesPickModal}
          addFile={(url) => setPromptAddition(url)}
        />
      )}

      {userError && (
        <Box ta="right">
          <ErrorMessage message={userError} />
        </Box>
      )}
    </Box>
  );
};

export default AiChat;
